/**
 * AI 에이전트 패널과 **다른 화면 사이의 다리**(2026-10-13).
 *
 * 에이전트가 사례 등록 화면 같은 창을 띄우면 그 창이 화면을 덮어 패널이 가려진다 — 사용자는 에이전트가 지금 무엇을 하는지
 * 알 수 없고, 멈추거나 말을 걸 수도 없었다. 대화의 상태는 패널(`useAssistantThread`)이 쥐고 있으므로, 패널이 여기에 상태와
 * 동작을 올려 두고 다른 창이 그것을 읽는다. 대화는 여전히 **하나**다 — 다른 창이 두 번째 대화를 만들지 않는다.
 *
 *   패널  → publish({ available, busy, activity, lastReply }) · setActions({ send, stop })
 *   다른 창 → useAgentBridge() 로 상태를 읽고 askAgent(text, { screen }) · stopAgent() 를 부른다
 *
 *  · `screen` 은 그 턴에만 모델에게 함께 가는 화면의 내용(지금 양식)이다 — 말풍선에는 사용자의 글만 남는다.
 *  · 패널이 없으면(닫혔거나 AI 제외 빌드) `available: false` 다 — 부르는 쪽이 줄을 숨긴다.
 */
import { useEffect, useState } from 'react';

const EMPTY = { available: false, busy: false, waiting: false, activity: '', lastReply: '', threadId: null };
let state = EMPTY;
let actions = null;
const listeners = new Set();

export function publishAgent(patch) {
  const next = { ...state, ...patch };
  if (Object.keys(next).every(k => next[k] === state[k])) return;
  state = next;
  listeners.forEach((fn) => { try { fn(state); } catch (_e) { /* 한 구독자의 오류가 다른 구독자를 막지 않게 */ } });
}

/** 패널이 올려 두는 동작. 패널이 사라지면 null 로 되돌린다 */
export function setAgentActions(next) {
  actions = next || null;
  if (!actions) publishAgent(EMPTY);
}

export const agentState = () => state;

/**
 * 같은 대화로 요청을 보낸다. @returns 보냈으면 true(패널이 없거나 작업 중이면 false)
 * @param opts.screen { kind, text } 그 턴에만 모델에게 함께 갈 화면의 종류와 내용. kind 는 서버가 아는 값이어야 한다(systemPrompt.SCREEN_KINDS)
 */
export function askAgent(text, opts = {}) {
  const t = String(text || '').trim();
  if (!t || !actions || typeof actions.send !== 'function' || state.busy) return false;
  const sc = opts.screen && typeof opts.screen === 'object' && opts.screen.kind ? { kind: String(opts.screen.kind), text: String(opts.screen.text || '') } : null;
  return actions.send(t, { screen: sc }) !== false;
}

export function stopAgent() {
  if (actions && typeof actions.stop === 'function') actions.stop();
}

export function useAgentBridge() {
  const [s, setS] = useState(state);
  useEffect(() => {
    listeners.add(setS);
    setS(state);
    return () => { listeners.delete(setS); };
  }, []);
  return s;
}
